import dayjs from "dayjs";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { DecimalHelper, StringHelper } from "@/helpers";
import {
  TransferRead,
  TransferHelper,
  TransferClassHelper,
  TransferDepositStatusI18nKeys,
} from "@/domains/transfer";
import { useAppDispatch, useAppSelector } from "@/stores";
import { tenantMemberDepositSelects, tenantMemberDepositThunks } from "./store";
// components
import { Badge, Popconfirm, Table } from "antd";
import { ColumnsType } from "antd/es/table";
import {
  ExportOutlined,
  EyeOutlined,
  IdcardOutlined,
  ShoppingOutlined,
  SwapRightOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { CopyIconButton } from "@/components";
import { TableTopFilters } from "./TableTopFilters";
import { CheckModal, CheckModalRef } from "./CheckModal";

export const TenantMemberDepositListPage = () => {
  const checkModalRef = useRef<CheckModalRef>(null);

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const loading = useAppSelector(tenantMemberDepositSelects.loading);

  const queryParams = useAppSelector(tenantMemberDepositSelects.queryParams);

  const transfers = useAppSelector(tenantMemberDepositSelects.transfers);

  useEffect(() => {
    void dispatch(tenantMemberDepositThunks.list(queryParams));
  }, [dispatch, queryParams]);

  const getRowClassName = (record: TransferRead) => {
    if (TransferHelper.isInvalid(record.status)) {
      return "fg:disabled v:top";
    }
    return "";
  };

  const handleCheckModalCheck = () => {
    void dispatch(tenantMemberDepositThunks.list(queryParams));
  };

  const columns: ColumnsType<TransferRead> = [
    {
      width: 70,
      className: "v:top",
      title: t("domains.Transfer.id"),
      dataIndex: "id",
      render(_, { id }) {
        return (
          <div className="word-break:break-all">
            <span className="mr:4">{id}</span>
            <CopyIconButton text={id} />
          </div>
        );
      },
    },
    {
      width: 260,
      className: "v:top",
      title: `${t("domains.Transfer.orderId")} | ${t(
        "domains.Transfer.memberInfo"
      )}`,
      key: "order",
      render(_, { order, member }) {
        return (
          <>
            <div className="word-break:break-all">
              <ShoppingOutlined className="mr:8" />
              {order?.id && StringHelper.getLength(order?.id) > 28 ? (
                <>
                  <span className="inline-block w:197 mr:8">
                    {StringHelper.ellipsisMiddle(order?.id, 28)}
                  </span>
                  <Popconfirm
                    title={t("domains.Transfer.orderId")}
                    description={
                      <>
                        <span className="mr:8 mb:8">{order?.id}</span>
                        <CopyIconButton text={order?.id} />
                      </>
                    }
                    showCancel={false}
                    okText={t("systems.Action.confirm")}
                    className="mr:8"
                  >
                    <ExportOutlined className="abs right:3 mt:4" />
                  </Popconfirm>
                  <CopyIconButton text={order?.id} />
                </>
              ) : (
                <>
                  <span className="inline-block w:197 mr:8">
                    {order?.id ?? "?"}
                  </span>
                  <CopyIconButton text={order?.id} />
                </>
              )}
            </div>
            <div className="word-break:break-all">
              <IdcardOutlined className="mr:8" />
              {member?.name && StringHelper.getLength(member?.name) > 28 ? (
                <>
                  <span className="inline-block w:175 mr:8">
                    {StringHelper.ellipsisEnd(member?.name, 28)}
                  </span>
                  <Popconfirm
                    title={t("domains.Transfer.memberName")}
                    description={
                      <>
                        <span className="mr:8 mb:8">{member?.name}</span>
                        <CopyIconButton text={member?.name} />
                      </>
                    }
                    showCancel={false}
                    okText={t("systems.Action.confirm")}
                    className="mr:8"
                  >
                    <ExportOutlined />
                  </Popconfirm>
                </>
              ) : (
                <>
                  <span className="inline-block w:197 mr:8">
                    {`${member?.name ?? "?"}`}
                  </span>
                  <CopyIconButton text={member?.name} />
                </>
              )}
            </div>
            <div className="word-break:break-all">
              <UserOutlined className="mr:8" />
              {member?.username &&
              StringHelper.getLength(member?.username) > 28 ? (
                <>
                  <span className="inline-block w:197 mr:8">
                    {StringHelper.ellipsisEnd(member?.username, 28)}
                  </span>
                  <Popconfirm
                    title={t("domains.Transfer.memberUsername")}
                    description={
                      <>
                        <span className="mr:8 mb:8">{member?.username}</span>
                        <CopyIconButton text={member?.username} />
                      </>
                    }
                    showCancel={false}
                    okText={t("systems.Action.confirm")}
                    className="mr:8"
                  >
                    <ExportOutlined />
                  </Popconfirm>
                </>
              ) : (
                <>
                  <span className="inline-block w:197 mr:8">
                    {`${member?.username ?? "?"}`}
                  </span>
                  <CopyIconButton text={member?.username} />
                </>
              )}
            </div>
          </>
        );
      },
    },
    {
      width: 340,
      className: "v:top",
      title() {
        return (
          <div className="flex gap:8">
            {t("domains.Transfer.currency")} |{" "}
            {t("domains.Transfer.exchangeRate")} |{" "}
            {t("domains.Transfer.sourceAddress")} <SwapRightOutlined />{" "}
            {t("domains.Transfer.targetAddress")}
          </div>
        );
      },
      render(_, record) {
        return (
          <div>
            {record.currency}
            {parseFloat(record.rate).toFixed(0) != "1" && (
              <Badge
                className="ml:8"
                count={record.rate}
                overflowCount={999999}
                color="#53a168"
              />
            )}
            <div className="flex">
              {record.source?.address ? (
                <>
                  {record.source?.address != null &&
                  (record.source?.address.length != 34 ||
                    record.source?.address[0] != "T") ? (
                    <span className="mr:4 fg:gray-60 text:line-through">
                      {record.source?.address}
                    </span>
                  ) : (
                    <>
                      <a
                        target="_blank"
                        href={TransferHelper.getAddressTransfersLink(
                          record.source?.address
                        )}
                      >
                        <span className="mr:4">{record.source?.address}</span>
                      </a>
                      <CopyIconButton
                        className="mr:4"
                        text={record.source?.address}
                      />
                    </>
                  )}
                </>
              ) : (
                "?"
              )}
              <SwapRightOutlined className="ml:4" />
            </div>
            <div className="flex">
              {record.target?.address ? (
                <>
                  {record.target?.address != null &&
                  (record.target?.address.length != 34 ||
                    record.target?.address[0] != "T") ? (
                    <span className="mr:4 fg:gray-60 text:line-through">
                      {record.target?.address}
                    </span>
                  ) : (
                    <>
                      <a
                        target="_blank"
                        href={TransferHelper.getAddressTransfersLink(
                          record.target?.address
                        )}
                      >
                        <span className="mr:4">{record.target?.address}</span>
                      </a>
                      <CopyIconButton
                        className="mr:4"
                        text={record.target?.address}
                      />
                    </>
                  )}
                </>
              ) : (
                <></>
              )}
            </div>
          </div>
        );
      },
    },
    {
      width: 90,
      className: "v:top t:right",
      title: t("domains.Transfer.depositAmount"),
      key: "amount",
      sorter: () => 1,
      render(_, { currency, amount0, amount }) {
        return (
          <>
            <div className="t:right">{DecimalHelper.fixed(amount, 2)}</div>
            <div className="t:right">{currency}</div>
            {amount0 !== amount && (
              <div className="t:right fg:gray-58">
                {DecimalHelper.fixed(amount0, 2)}
              </div>
            )}
          </>
        );
      },
    },
    {
      width: 90,
      className: "v:top t:right",
      title: t("domains.Transfer.depositResultAmount"),
      key: "result",
      sorter: () => 1,
      render(_, { currency, result0, result }) {
        return (
          result && (
            <>
              <div className="t:right">{DecimalHelper.fixed(result, 2)}</div>
              <div className="t:right">{currency}</div>
              {result0 !== result && (
                <div className="t:right fg:gray-58">
                  {DecimalHelper.fixed(result0, 2)}
                </div>
              )}
            </>
          )
        );
      },
    },
    {
      width: 80,
      align: "right",
      className: "v:top",
      title: t("domains.Transfer.chargeAmount"),
      key: "charge",
      render(_, { currency, charge }) {
        return (
          charge && (
            <>
              <div className="t:right">{DecimalHelper.fixed(charge, 2)}</div>
              <div className="t:right">{currency}</div>
            </>
          )
        );
      },
    },
    {
      className: "v:top",
      title: t("domains.Transfer.note"),
      key: "note",
      render(_, { note }) {
        return (
          <>
            <div className="w:full h:6.2857em overflow:hidden">
              {note && StringHelper.getLength(note) > 108
                ? StringHelper.ellipsisEnd(note, 108)
                : note}
            </div>
            {note && StringHelper.getLength(note) > 108 && (
              <Popconfirm
                className="abs bottom:8 right:4 bg:white p:5"
                title={t("systems.Modal.note")}
                description={
                  <>
                    <span className="inline-block w:400 mr:8 mb:8 word-break:break-all">
                      {note}
                    </span>
                  </>
                }
                showCancel={false}
                okText={t("systems.Action.confirm")}
              >
                <ExportOutlined />
              </Popconfirm>
            )}
          </>
        );
      },
    },
    {
      width: 90,
      align: "right",
      className: "v:top",
      title: t("domains.Transfer.depositCreatedAt"),
      key: "created_at",
      sorter: () => 1,
      render(_, { created_at }) {
        return <div>{dayjs.$ldts(created_at)}</div>;
      },
    },
    {
      width: 90,
      align: "right",
      className: "v:top",
      title: t("domains.Transfer.checkedAt"),
      key: "checked_at",
      sorter: () => 1,
      render(_, { checked_at }) {
        return checked_at ? <div>{dayjs.$ldts(checked_at)}</div> : null;
      },
    },
    {
      width: 90,
      align: "right",
      className: "v:top",
      title: t("domains.Transfer.arrivedAt"),
      key: "arrived_at",
      sorter: () => 1,
      render(_, { arrived_at }) {
        return arrived_at ? <div>{dayjs.$ldts(arrived_at)}</div> : null;
      },
    },
    {
      width: 145,
      className: "v:top",
      title: t("domains.Transfer.status"),
      key: "status",
      render(_, record) {
        const TransactionEyeLink = ({ tran }: { tran: string }) => (
          <a
            className="ml:8"
            target="_blank"
            href={TransferHelper.getTransactionLink(tran)}
          >
            <EyeOutlined />
          </a>
        );

        return (
          <div>
            <span className={TransferClassHelper.getStatusColor(record.status)}>
              {t(TransferDepositStatusI18nKeys[record.status])}
            </span>
            <span className="ml:8">
              <CopyIconButton text={record.transaction} />
            </span>
            {record.transaction &&
              record.transaction.split(",").map((tran, idx) => {
                return <TransactionEyeLink tran={tran} key={idx} />;
              })}
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex flex:col">
      <TableTopFilters />

      <Table
        rowKey="id"
        tableLayout="fixed"
        loading={loading}
        columns={columns}
        dataSource={transfers}
        scroll={{ x: "1600px" }}
        rowClassName={getRowClassName}
      ></Table>

      <CheckModal ref={checkModalRef} onCheck={handleCheckModalCheck} />
    </div>
  );
};
